(ns common.schema.entities.screening-profile.materialization
  (:require
   [clojure.string :as string]
   [clojure.edn :as edn]
   [common.schema.entities.workspace.kyc.kyc-utils :as kyc-utils]
   [common.schema.entities.workspace.kyc.templates.templates :as kyc-templates]
   [common.schema.entities.counterparty-diligence.templates.registry :as  counterparty-diligence-template-registry]
   [common.util :as cu]
   [clojure.set :as set]))

(defn- nationality-countries
  "Country keywords of a KYC person's nationalities (legacy single-answer
   fallback included), first entry first. Screening supports several
   countries per person."
  [answers nationality-keys]
  (->> (kyc-utils/nationality-rows answers nationality-keys)
       (keep :nationality)
       vec))

(defn person-has-first-and-last-name?
  [person]
  (let [{:keys [first-name last-name]} person]
    (and (string? first-name)
         (string? last-name)
         (-> first-name
             string/trim
             seq)
         (-> last-name
             string/trim
             seq))))

(defn legal-entity-has-company-name?
  [legal-entity]
  (let [company-name (:company-name legal-entity)]
    (and (string? company-name)
         (-> company-name
             string/trim
             seq))))

(defn person->screening-countries
  "Splits a materialized person's country data into the primary screening
   country and the additional countries (mapped to RiskScreen Country2/3;
   countries beyond those are not screened)."
  [{:keys [country countries]}]
  (let [primary (or country (first countries))]
    {:country              primary
     :additional-countries (some->> countries
                                    (remove #{primary})
                                    distinct
                                    seq
                                    vec)}))

(defn person-profile->deduplication-id
  [screening-profile]
  (cu/match-key-of [(:screening-profile/first-name screening-profile)
                    (:screening-profile/last-name screening-profile)
                    (:screening-profile/date-of-birth screening-profile)
                    (:screening-profile/country screening-profile)]))

(defn company-profile->deduplication-id
  [screening-profile]
  (cu/match-key-of [(:screening-profile/company-name screening-profile)
                    (:screening-profile/business-id screening-profile)]))

(defn screening-profile->deduplication-id
  [screening-profile]
  (case (:screening-profile/type screening-profile)
    :screening-profile-type/person
    (person-profile->deduplication-id screening-profile)

    :screening-profile-type/company
    (company-profile->deduplication-id screening-profile)))

(defn person->deduplication-id
  "Pairs with `person-profile->deduplication-id`."
  [person]
  (cu/match-key-of [(:first-name person)
                    (:last-name person)
                    (:date-of-birth person)
                    (:country person)]))

(defn legal-entity->deduplication-id
  "Pairs with `company-profile->deduplication-id`, so a legal entity read from
   KYC or counterparty diligence answers finds the profile the backend would
   reuse for it."
  [legal-entity]
  (cu/match-key-of [(:company-name legal-entity)
                    (:business-id legal-entity)]))

(defn find-legal-entity-from-kyc-process
  [kyc-process]
  (let [{:keys [kyc-process/answers kyc-process/template]} kyc-process
        kyc-profile (:common/containing-entity kyc-process)
        kyc-profile-id (or (:db/id kyc-profile)
                           kyc-profile)

        legal-entity (cond
                       (some #{template} kyc-templates/legal-entity-template-keys)
                       {:company-name (:kyc-legal-entity/name answers)
                        :business-id  (:kyc-legal-entity/business-id answers)
                        :country      (:kyc-legal-entity/jurisdiction answers)
                        :kyc-profile  kyc-profile-id
                        :kyc-profiles #{kyc-profile-id}}

                       (some #{template} kyc-templates/tenant-template-keys)
                       {:company-name (:kyc-tenant/name answers)
                        :business-id  (:kyc-tenant/business-id answers)
                        :country      (:kyc-tenant/domicile answers)
                        :kyc-profile  kyc-profile-id
                        :kyc-profiles #{kyc-profile-id}}

                       (some #{template} kyc-templates/contract-template-keys)
                       {:company-name (:contract-kyc/business-name answers)
                        :business-id  (:contract-kyc/business-id answers)
                        :country      (:contract-kyc/domicile answers)
                        :kyc-profile  kyc-profile-id
                        :kyc-profiles #{kyc-profile-id}}

                       (some #{template} kyc-templates/natural-person-templates-keys)
                       ; Todo add more if needed ??
                       nil)]
    (when (legal-entity-has-company-name? legal-entity)
      legal-entity)))

(defn find-persons-from-kyc-process
  [kyc-process]
  (let [template (:kyc-process/template kyc-process)
        answers (:kyc-process/answers kyc-process)
        answers (cond (map? answers) answers
                      (string? answers) (edn/read-string answers))
        kyc-profile (:common/containing-entity kyc-process)

        kyc-profile-id (or (:db/id kyc-profile)
                           kyc-profile)

        get-ubo-person (fn [ubo]
                         (let [also-pep? (= (:kyc-ubo/pep ubo) :yes)
                               roles (if also-pep?
                                       #{:pep :ubo}
                                       #{:ubo})
                               countries (nationality-countries ubo kyc-utils/ubo-nationality-keys)]
                           {:first-name    (:kyc-ubo/first-name ubo)
                            :last-name     (:kyc-ubo/last-name ubo)
                            :date-of-birth (:kyc-ubo/dob ubo)
                            :country       (first countries)
                            :countries     countries
                            :roles         roles}))

        get-pep-person (fn [pep]
                         ;; Prefer the structured name answers; fall back to
                         ;; splitting the legacy single full-name answer for
                         ;; entries saved before the name split.
                         (let [name (or (:kyc-ubo.pep/name pep) "") ; To avoid NullPointerExceptions
                               name-parts (string/split name #"\s")
                               legacy-name? (< 1 (count name-parts))
                               first-name (or (not-empty (str (:kyc-ubo.pep/first-name pep)))
                                              (when legacy-name?
                                                (first name-parts)))
                               last-name (or (not-empty (str (:kyc-ubo.pep/last-name pep)))
                                             (when legacy-name?
                                               (last name-parts)))]
                           (when (and first-name
                                      last-name)
                             {:first-name    first-name
                              :last-name     last-name
                              :date-of-birth (:kyc-ubo.pep/dob pep)
                              :country       (:kyc-ubo.pep/birth-country pep)
                              :roles         #{:pep}})))

        get-board-member-person (fn [board-member]
                                  (let [confirmation-status
                                        (:kyc-board-member/confirmation-status board-member)
                                        countries (nationality-countries
                                                   board-member
                                                   kyc-utils/board-member-nationality-keys)]
                                    (when-not (or (= confirmation-status :removed-by-customer)
                                                  (= confirmation-status :removed-by-controller))
                                      {:first-name    (:kyc-board-member/first-name board-member)
                                       :last-name     (:kyc-board-member/last-name board-member)
                                       :date-of-birth (:kyc-board-member/dob board-member)
                                       :country       (first countries)
                                       :countries     countries
                                       :roles         #{:board-member}})))

        persons (cond
                  (some #{template} kyc-templates/legal-entity-template-keys)
                  (let [ubos (->> answers
                                  :kyc-legal-entity/ubo-edn
                                  (map get-ubo-person))
                        peps (->> answers
                                  :kyc-legal-entity/pep-edn
                                  (map get-pep-person))

                        board-members (->> answers
                                           :kyc-legal-entity/board-members-edn
                                           (map get-board-member-person))]
                    (vec (concat ubos peps board-members)))

                  (some #{template} kyc-templates/natural-person-templates-keys)
                  (let [countries (nationality-countries answers
                                                         kyc-utils/person-nationality-keys)
                        legal-entity-person
                        (when (and (:kyc-person/first-name answers)
                                   (:kyc-person/last-name answers))
                          {:first-name    (:kyc-person/first-name answers)
                           :last-name     (:kyc-person/last-name answers)
                           :date-of-birth (:kyc-person/dob answers)
                           :country       (first countries)
                           :countries     countries
                           :roles         #{:legal-entity-person}})]
                    [legal-entity-person])

                  (some #{template} kyc-templates/tenant-template-keys)
                  (->> answers
                       :kyc-tenant/ubo-edn
                       (mapv get-ubo-person))

                  (some #{template} kyc-templates/contract-template-keys)
                  (->> answers
                       :contract-kyc/ubo-edn
                       (mapv get-ubo-person))

                  :else [])

        persons-with-roles (->> persons
                                (filterv person-has-first-and-last-name?)
                                (group-by (fn [{:keys [first-name last-name
                                                       date-of-birth country]}]
                                            (cu/match-key-of [first-name last-name
                                                              date-of-birth country])))
                                (vals)
                                (mapv (fn [person-array]
                                        (let [roles (->> person-array
                                                         (mapcat :roles)
                                                         (into #{}))]
                                          (some-> person-array
                                                  first
                                                  (dissoc :role)
                                                  (assoc :roles roles
                                                         :kyc-profile kyc-profile-id
                                                         :kyc-profiles #{kyc-profile-id}))))))]
    persons-with-roles))

(defn find-legal-entities-from-counterparty-diligence
  [counterparty-diligence process]
  (let [diligence-id (:db/id counterparty-diligence)
        template (counterparty-diligence-template-registry/resolve-template counterparty-diligence)
        template-id (:template/id template)
        answers (:counterparty-diligence-process/answers process)
        answers (cond (map? answers) answers
                      (string? answers) (edn/read-string answers))]
    (cond
      (some #{template-id} counterparty-diligence-template-registry/project-template-keys)
      (let [find-legal-entities-fn (:template/find-legal-entities-with-key-persons template)]
        (->> (find-legal-entities-fn {:data answers})
             (filterv legal-entity-has-company-name?)
             (mapv (fn [legal-entity]
                     (-> legal-entity
                         (assoc :counterparty-diligences #{diligence-id})
                         (update :key-persons
                                 (fn [key-persons]
                                   (->> key-persons
                                        (mapv (fn [person]
                                                (assoc person
                                                       :counterparty-diligences #{diligence-id})))))))))))

      (some #{template-id} counterparty-diligence-template-registry/legal-entity-template-keys)
      (let [{:keys [_id legal-entity/company-name
                    legal-entity/business-id
                    legal-entity/jurisdiction]} answers

            company-name (cu/str-trim-unicode company-name)

            find-key-persons-fn (:template/find-key-persons template)
            key-persons (->> (find-key-persons-fn {:data answers})
                             (mapv (fn [person]
                                     (assoc person
                                            :counterparty-diligences #{diligence-id}))))

            legal-entity {:_id          (cu/match-key-of [company-name business-id])
                          :company-name company-name
                          :business-id  business-id
                          :country      jurisdiction
                          :key-persons  key-persons
                          :counterparty-diligences #{diligence-id}}]
        (if (legal-entity-has-company-name? legal-entity)
          [legal-entity]
          [])))))

;; For project templates, this function is only used in frontend,
;; where legal entity key persons are embedded in the legal entity.
;; If project diligences can have continuous screening later and
;; we want to use this function in daily screening sync,
;; we must also find the legal entity key persons instead of just natural persons
(defn find-persons-from-counterparty-diligence
  [counterparty-diligence process]
  (let [diligence-id (:db/id counterparty-diligence)
        template (counterparty-diligence-template-registry/resolve-template counterparty-diligence)
        template-id (:template/id template)
        answers (:counterparty-diligence-process/answers process)
        answers (cond (map? answers) answers
                      (string? answers) (edn/read-string answers)
                      :else {})]
    (cond
      (some #{template-id} counterparty-diligence-template-registry/natural-person-template-keys)
      (let [find-person-fn (:template/find-person template)
            person (when find-person-fn
                     (find-person-fn {:data answers}))]
        (if (person-has-first-and-last-name? person)
          [(assoc person
                  :_id (person->deduplication-id person)
                  :counterparty-diligences #{diligence-id})]
          []))

      (some #{template-id} counterparty-diligence-template-registry/project-template-keys)
      (let [find-natural-persons-fn (:template/find-natural-persons template)]
        (->> (find-natural-persons-fn {:data answers})
             (filterv person-has-first-and-last-name?)
             (mapv (fn [person]
                     (assoc person
                            :counterparty-diligences #{diligence-id})))))

      (some #{template-id} counterparty-diligence-template-registry/legal-entity-template-keys)
      (let [find-key-persons-fn (:template/find-key-persons template)]
        (->> (find-key-persons-fn {:data answers})
             (filterv person-has-first-and-last-name?)
             (mapv (fn [person]
                     (assoc person
                            :counterparty-diligences #{diligence-id}))))))))

(defn screening-profile->kyc-profiles-and-pep-statuses
  "Extract KYC profiles (regular, tenant, and contract) from a screening profile."
  [{:keys [screening-profile entity-lookup display-string]}]
  (let [person-screening-profile? (= (:screening-profile/type screening-profile)
                                     :screening-profile-type/person)
        deduplication-id (screening-profile->deduplication-id screening-profile)
        kyc-profiles (->> screening-profile
                          :screening-profile/kyc-profiles
                          (mapv (fn [id-or-entity]
                                  (entity-lookup (or (:db/id id-or-entity)
                                                     id-or-entity))))
                          (remove (fn [kyc-profile]
                                    (or (not kyc-profile)
                                        (:common/disabled? kyc-profile)))))

        latest-approved-processes
        (->> kyc-profiles
             (mapv (fn [kyc-profile]
                     (let [entity-type (:entity/type kyc-profile)

                           kyc-processes-attr (case entity-type
                                                :entity.type/tenant-kyc-profile :tenant-kyc-profile/kyc-processes
                                                :entity.type/contract-kyc-profile :contract-kyc-profile/kyc-processes
                                                :kyc-profile/kyc-processes)
                           latest-approved-process
                           (->> (get kyc-profile kyc-processes-attr)
                                (mapv (fn [id-or-entity]
                                        (entity-lookup (or (:db/id id-or-entity)
                                                           id-or-entity))))
                                (filterv (fn [kyc-process]
                                           (->> kyc-process
                                                :kyc-process/status
                                                (contains? #{:kyc-process.status/approved
                                                             :kyc-process.status/approved-modified}))))
                                (remove :common/disabled?)
                                (sort-by :common/created-on)
                                last)]
                       (when latest-approved-process
                         (assoc (select-keys latest-approved-process (conj (keys latest-approved-process) :db/id))
                                :common/containing-entity kyc-profile)))))
             (remove nil?))

        data (->> latest-approved-processes
                  (mapv (fn [kyc-process]
                          (let [kyc-profile (:common/containing-entity kyc-process)
                                entity-type (:entity/type kyc-profile)

                                target-id (case entity-type
                                            :entity.type/tenant-kyc-profile (:tenant-kyc-profile/target kyc-profile)
                                            :entity.type/contract-kyc-profile (:contract-kyc-profile/target kyc-profile)
                                            (:kyc-profile/target kyc-profile))
                                target (entity-lookup target-id)
                                target-label (display-string target :short entity-lookup)
                                base-data {:kyc-profile-id (:db/id kyc-profile)
                                           :kyc-profile-type entity-type
                                           :kyc-profile-label target-label}]
                            (if-not person-screening-profile?
                              base-data
                              (let [all-persons (find-persons-from-kyc-process kyc-process)
                                    person-matches (->> all-persons
                                                        (filterv (fn [person]
                                                                   (= (person->deduplication-id person)
                                                                      deduplication-id))))
                                    roles (->> person-matches
                                               (mapv (fn [{:keys [roles]}]
                                                       (set roles)))
                                               (apply set/union))
                                    pep? (or (contains? roles :pep)
                                             (contains? roles :politically-exposed-person))]
                                (assoc base-data :pep? pep?))))))
                  (sort-by (fn [{:keys [kyc-profile-label]}]
                             (string/lower-case (or kyc-profile-label ""))))
                  vec)]
    data))

(defn screening-profile->counterparty-diligences-and-pep-statuses
  [{:keys [screening-profile entity-lookup display-string]}]
  (let [person-screening-profile? (= (:screening-profile/type screening-profile)
                                     :screening-profile-type/person)
        deduplication-id (screening-profile->deduplication-id screening-profile)

        counterparty-diligences (->> screening-profile
                                     :screening-profile/counterparty-diligences
                                     (mapv (fn [id-or-entity]
                                             (entity-lookup (or (:db/id id-or-entity)
                                                                id-or-entity))))
                                     (remove nil?))

        latest-approved-processes
        (->> counterparty-diligences
             (mapv (fn [counterparty-diligence]
                     (let [latest-approved-process
                           (->> counterparty-diligence
                                :counterparty-diligence/processes
                                (mapv (fn [id-or-entity]
                                        (entity-lookup (or (:db/id id-or-entity)
                                                           id-or-entity))))
                                (filterv (fn [process]
                                           (->> process
                                                :counterparty-diligence-process/status
                                                (contains? #{:counterparty-diligence-process.status/completed}))))
                                (remove :common/disabled?)
                                (sort-by :common/created-on)
                                last)]
                       (when latest-approved-process
                         (assoc (select-keys latest-approved-process (conj (keys latest-approved-process) :db/id))
                                :common/containing-entity counterparty-diligence)))))
             (remove nil?))

        data (->> latest-approved-processes
                  (mapv (fn [process]
                          (let [counterparty-diligence (:common/containing-entity process)
                                base-data {:counterparty-diligence-id (:db/id counterparty-diligence)
                                           :counterparty-diligence-label (display-string counterparty-diligence)
                                           :counterparty-diligence-type (:entity/type counterparty-diligence)}]
                            (if-not person-screening-profile?
                              base-data
                              (let [all-persons (find-persons-from-counterparty-diligence counterparty-diligence process)
                                    person-matches (->> all-persons
                                                        (filterv (fn [person]
                                                                   (= (person->deduplication-id person)
                                                                      deduplication-id))))
                                    roles (->> person-matches
                                               (mapv (fn [person]
                                                       (-> person
                                                           :roles
                                                           set)))
                                               (apply set/union))
                                    pep? (or (contains? roles :pep)
                                             (contains? roles :politically-exposed-person))]
                                (assoc base-data :pep? pep?))))))
                  (sort-by (fn [{:keys [counterparty-diligence-label]}]
                             (string/lower-case counterparty-diligence-label)))
                  vec)]
    data))

(defn calculate-pep-or-rca-mismatch-data
  [{:keys [screening-profile
           kyc-profiles-and-pep-statuses
           counterparty-diligences-and-pep-statuses]}]
  (when (-> screening-profile
            :screening-profile/type
            (= :screening-profile-type/person))
    (let [pep-or-rca-in-screening-service? (or (-> screening-profile
                                                   :screening-profile/pep-status
                                                   boolean)
                                               (-> screening-profile
                                                   :screening-profile/rca-status
                                                   boolean))]
      (if pep-or-rca-in-screening-service?
        (let [pep-in-every-kyc-profile? (->> kyc-profiles-and-pep-statuses
                                             (every? :pep?))
              pep-in-every-counterparty-diligence? (->> counterparty-diligences-and-pep-statuses
                                                        (every? :pep?))
              mismatch? (not (and pep-in-every-kyc-profile?
                                  pep-in-every-counterparty-diligence?))]
          {:mismatch? mismatch?
           :pep-or-rca-in-screening-service? true
           :pep-in-every-kyc-profile? pep-in-every-kyc-profile?
           :pep-in-every-counterparty-diligence? pep-in-every-counterparty-diligence?})

        (let [pep-in-some-kyc-profile? (->> kyc-profiles-and-pep-statuses
                                            (some :pep?))
              pep-in-some-counterparty-diligence? (->> counterparty-diligences-and-pep-statuses
                                                       (some :pep?))
              mismatch? (or pep-in-some-kyc-profile?
                            pep-in-some-counterparty-diligence?)]
          {:mismatch? mismatch?
           :pep-or-rca-in-screening-service? false
           :pep-in-some-kyc-profile? pep-in-some-kyc-profile?
           :pep-in-some-counterparty-diligence? pep-in-some-counterparty-diligence?})))))
